// CodexScreen (§4) — the full element compendium: search + tier filters, a grid
// of every element (locked vs unlocked), and a recipe-reveal bottom sheet.
// Replaces ElementGrid + the library modal + InfoPanel.
import { AD_SLOTS } from "../../config/adSlots";
import React, { useMemo, useState } from "react";
import Screen from "../ui/Screen";
import ElementOrb, { tierColor, tierName, TIER_NAME } from "../ui/ElementOrb";
import { Chip, BottomSheet } from "../ui/primitives";
import { useElementStore } from "../../store/elementStore";
import { levelFromDiscovered, getStreak } from "../../game/stats";
import { getLocalFunFact } from "../../services/funFactService";
import { ELEMENT_DEFS } from "../elementDefinitions";

const LockIcon = ({ size = 16, stroke = "#5A5F7E" }: { size?: number; stroke?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const CodexScreen: React.FC = () => {
  const unlocked = useElementStore((s) => s.unlocked);
  const [query, setQuery] = useState("");
  const [tier, setTier] = useState<string>("all");
  const [inspectId, setInspectId] = useState<string | null>(null);

  const discovered = unlocked.size;
  const level = levelFromDiscovered(discovered);
  const streak = getStreak().streak;

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(ELEMENT_DEFS)
      .filter((e) => (tier === "all" || String(e.tier) === tier))
      .filter((e) => {
        if (!q) return true;
        // locked elements are only searchable once discovered (no spoilers)
        return unlocked.has(e.id) && e.name.toLowerCase().includes(q);
      })
      .sort((a, b) => (a.tier - b.tier) || a.name.localeCompare(b.name));
  }, [query, tier, unlocked]);

  const tierChips = [{ key: "all", label: "All" }, ...[1, 2, 3, 4, 5, 6, 7].map((t) => ({ key: String(t), label: TIER_NAME[t] }))];

  const inspectDef = inspectId ? ELEMENT_DEFS[inspectId] : null;
  const inspectUnlocked = inspectId ? unlocked.has(inspectId) : false;
  const recipeText = useMemo(() => {
    if (!inspectDef || !inspectUnlocked || inspectDef.recipe.length === 0) return null;
    return inspectDef.recipe.map((r) => ELEMENT_DEFS[r]?.name ?? r).join(" + ");
  }, [inspectDef, inspectUnlocked]);

  return (
    <Screen title="Codex" active="codex" streak={streak} level={level} ad={AD_SLOTS.codex}>
      <div className="mc-in" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        {/* search */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 14px", height: 46, borderRadius: 14, background: "var(--surface)", border: "1px solid var(--border)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search discovered elements…"
            aria-label="Search elements"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontFamily: "'DM Sans',sans-serif", fontSize: 14, minWidth: 0 }}
          />
        </div>

        {/* tier filters */}
        <div className="mc-scroll" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
          {tierChips.map((c) => (
            <Chip key={c.key} label={c.label} active={tier === c.key} onClick={() => setTier(c.key)} />
          ))}
        </div>

        <div style={{ fontSize: 12, color: "var(--text-3)" }}>
          {discovered} / {Object.keys(ELEMENT_DEFS).length} discovered
          {tier !== "all" && ` · ${TIER_NAME[Number(tier)]}`}
        </div>

        {/* grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(76px,1fr))", gap: 12 }}>
          {list.map((e) => {
            const isUnlocked = unlocked.has(e.id);
            return (
              <button
                key={e.id}
                className="mc-tap"
                onClick={() => setInspectId(e.id)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "10px 4px", borderRadius: 16, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer", opacity: isUnlocked ? 1 : 0.72 }}
              >
                <div style={{ position: "relative", display: "inline-flex" }}>
                  <ElementOrb id={e.id} size={44} locked={!isUnlocked} />
                  {!isUnlocked && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <LockIcon />
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 11, color: isUnlocked ? "var(--text-2)" : "var(--text-3)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                  {isUnlocked ? e.name : "???"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* inspect / recipe reveal */}
      <BottomSheet open={!!inspectId} onClose={() => setInspectId(null)}>
        {inspectDef && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <ElementOrb id={inspectDef.id} size={56} withRing locked={!inspectUnlocked} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700 }}>{inspectUnlocked ? inspectDef.name : "Undiscovered"}</div>
                <span style={{ display: "inline-block", marginTop: 4, fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, color: tierColor(inspectDef.tier), background: `${tierColor(inspectDef.tier)}22`, border: `1px solid ${tierColor(inspectDef.tier)}55` }}>
                  {tierName(inspectDef.tier)}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--accent-text)" }}>Recipe</div>
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-2)" }}>
              {inspectDef.recipe.length === 0
                ? "A base element — no recipe."
                : inspectUnlocked
                ? recipeText
                : "Discover it to reveal the recipe."}
            </div>

            {inspectUnlocked && (
              <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                {getLocalFunFact(inspectDef.id, inspectDef.recipe) || `A ${tierName(inspectDef.tier).toLowerCase()} element in your codex.`}
              </div>
            )}
          </>
        )}
      </BottomSheet>
    </Screen>
  );
};

export default CodexScreen;
